import Chart from 'chart.js/auto';
import { chartColors } from './chartColors.js';
import { downloadChartCSV, formatTooltipLabel, getMaxYAxisForAnswerType } from './chartHelpers.js';

const questionDataCache = {};

async function fetchQuestionGroupData(surveyId, questionId, groupIds) {
    const queryParams = new URLSearchParams({
        survey_id: surveyId,
        question_id: questionId,
    });

    const groupIdsFiltered = groupIds.filter((id, index, self) => self.indexOf(id) === index);
    if (groupIdsFiltered.length > 0) {
        queryParams.append('group_ids', groupIdsFiltered.join(','));
    }

    const queryParamsString = queryParams.toString();

    if (questionDataCache[queryParamsString]) {
        return questionDataCache[queryParamsString];
    }

    const url = `${benchmarktool.api_url}/api/answers?${queryParamsString}`;
    const response = await fetch(url);
    const data = await response.json();

    questionDataCache[queryParamsString] = data;

    return data;
}

function generateDataset(label, data, backgroundColor) {
    return {
        label: label,
        data: data,
        backgroundColor: backgroundColor,
        fill: false,
        borderColor: 'backgroundColor',
        tension: 0.1,
    };
}

const updateChartFilters = (chart, groups) => {
    const categoryFilters = jQuery('.js-chart-category-filter');
    const yearFilters = jQuery('.js-chart-group-data-filter');

    const initializeDefaultCategory = questionId => {
        const activeFilterElement = jQuery(`[data-active-filter-id="${questionId}"]`);
        if (activeFilterElement.length > 0 && !activeFilterElement.attr('data-category')) {
            const firstCategory = Object.keys(groups)[0];
            if (firstCategory) {
                if (firstCategory.length > 20) {
                    activeFilterElement.text(firstCategory.substring(0, 24) + '...');
                } else {
                    activeFilterElement.text(firstCategory);
                }
                activeFilterElement.attr('data-category', firstCategory);
            }
        }
    };

    categoryFilters.each(function () {
        jQuery(this).click(function () {
            const surveyId = jQuery(this).data('survey-id');
            const questionId = jQuery(this).data('question-id');
            const activeCategory = jQuery(this).data('category-key');
            const filterToUpdate = jQuery.find(`[data-active-filter-id="` + questionId + `"]`);
            const yearFilterToUpdate = jQuery.find(`[data-year-filter-question-id="` + questionId + `"]`);

            if (filterToUpdate.length > 0) {
                jQuery(filterToUpdate).each(function () {
                    if (activeCategory.length > 20) {
                        jQuery(this).text(activeCategory.substring(0, 24) + '...');
                        jQuery(this).attr('data-category', activeCategory);
                    } else {
                        jQuery(this).text(activeCategory);
                        jQuery(this).attr('data-category', activeCategory);
                    }

                    if (!chart.canvas.id.includes(questionId)) {
                        return;
                    }

                    if (!chart.data.labels.includes(activeCategory)) {
                        const groupIds = [];
                        const addedTypes = new Set();

                        for (const group of groups[activeCategory]) {
                            let latestYearKey = 0;
                            const yearKeys = Object.keys(group.years);
                            if (yearKeys.length > 0) {
                                const numericKeys = yearKeys.map(Number).filter(key => !isNaN(key));
                                if (numericKeys.length > 0) {
                                    latestYearKey = Math.max(...numericKeys);
                                }
                            }

                            if (addedTypes.has(group.type)) {
                                continue;
                            }

                            groupIds.push({
                                year: group.years[latestYearKey],
                                type: group.type,
                            });

                            addedTypes.add(group.type);
                        }

                        fetchQuestionGroupData(
                            surveyId,
                            questionId,
                            groupIds.map(group => group.year),
                        ).then(res => {
                            const answers = Object.values(res)[0] || [];

                            const groupedAnswers = groupIds.map(group => {
                                return {
                                    type: group.type,
                                    answers: Array.isArray(answers)
                                        ? answers.filter(answer => answer.group_id === group.year)
                                        : [],
                                };
                            });

                            chart.data.labels = [];

                            chart.data.datasets.forEach(dataset => {
                                dataset.data = [];
                            });

                            groupedAnswers.forEach(group => {
                                chart.data.labels.push(group.type);
                            });

                            groupedAnswers.forEach((group, groupIndex) => {
                                group.answers.forEach(answer => {
                                    const dataset = chart.data.datasets.find(ds => ds.label === answer.answer_label);
                                    if (dataset) {
                                        while (dataset.data.length < groupIndex) {
                                            dataset.data.push(null);
                                        }
                                        dataset.data[groupIndex] = answer.answer_value;
                                    }
                                });
                            });

                            chart.data.datasets.forEach(dataset => {
                                while (dataset.data.length < chart.data.labels.length) {
                                    dataset.data.push(null);
                                }
                            });

                            chart.update();
                        });
                    }
                });
            }

            jQuery(yearFilterToUpdate).each(function () {
                if (jQuery(this).data('category') === activeCategory) {
                    jQuery(this).addClass('active');
                } else {
                    jQuery(this).removeClass('active');
                }
            });
        });
    });

    yearFilters.each(function () {
        jQuery(this).click(function (e) {
            const surveyId = jQuery(this).data('survey-id');
            const questionId = jQuery(this).data('question-id');
            const activeValue = jQuery(this).data('filter-value');
            const activeFilterElement = jQuery(`[data-active-filter-id="${questionId}"]`);
            const yearFilterToUpdate = jQuery(e.target).closest(`[data-year-filter-question-id="` + questionId + `"]`);

            if (!chart.canvas.id.includes(questionId)) {
                return;
            }

            initializeDefaultCategory(questionId);

            const activeCategory = activeFilterElement.attr('data-category') || activeFilterElement.text();
            if (!groups[activeCategory]) {
                return;
            }

            const groupIds = [];
            const addedTypes = new Set();

            for (const subLevel of Object.keys(groups[activeCategory])) {
                const type = groups[activeCategory][subLevel].type;

                if (addedTypes.has(type)) {
                    continue;
                }

                groupIds.push({
                    year: groups[activeCategory][subLevel].years[activeValue],
                    type: type,
                });

                addedTypes.add(type);
            }

            fetchQuestionGroupData(
                surveyId,
                questionId,
                groupIds.map(group => group.year),
            ).then(res => {
                if (res.length === 0) {
                    return;
                }

                const answers = Object.values(res)[0] || [];
                const groupedAnswers = groupIds.map(group => {
                    return {
                        type: group.type,
                        answers: Array.isArray(answers) ? answers.filter(answer => answer.group_id === group.year) : [],
                    };
                });

                chart.data.labels = [];

                chart.data.datasets.forEach(dataset => {
                    dataset.data = [];
                });

                groupedAnswers.forEach(group => {
                    chart.data.labels.push(group.type);
                });

                groupedAnswers.forEach((group, groupIndex) => {
                    group.answers.forEach(answer => {
                        const dataset = chart.data.datasets.find(ds => ds.label === answer.answer_label);
                        if (dataset) {
                            while (dataset.data.length < groupIndex) {
                                dataset.data.push(null);
                            }
                            dataset.data[groupIndex] = answer.answer_value;
                        }
                    });
                });

                chart.data.datasets.forEach(dataset => {
                    while (dataset.data.length < chart.data.labels.length) {
                        dataset.data.push(null);
                    }
                });

                chart.update();
            });

            if (yearFilterToUpdate.length > 0) {
                jQuery(yearFilterToUpdate).each(function () {
                    jQuery(this).find('.js-active-group-filter').text(activeValue);
                });
            }
        });
    });
};

class DefaultChartComponent extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
    }

    static get observedAttributes() {
        return ['survey_id', 'question_id', 'chart_data', 'chart_groups'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'chart_data') {
        } else {
            this[name.replace('-', '_')] = newValue;
        }
    }

    connectedCallback() {
        this.render();
        this.observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        this.buildChart();
                        this.observer.disconnect();
                    }
                });
            },
            { root: null, threshold: 0.1 },
        );

        this.observer.observe(this);
    }

    initChartDownloadCsv = () => {
        const downloadButton = document.querySelector('[data-download-button-question-id="' + this.questionId + '"]');

        if (!downloadButton) {
            return;
        }

        downloadButton.addEventListener('click', event => {
            event.preventDefault();

            const chartTitle = downloadButton.getAttribute('data-chart-title') || 'Chart';
            downloadChartCSV({
                chart: this.chart,
                shadowRoot: this.shadowRoot,
                filename: chartTitle,
            });
        });
    };

    buildChart() {
        const questionId = this.getAttribute('question_id');
        const answerType = this.getAttribute('data-answer-type');
        const data = JSON.parse(this.getAttribute('data-chart_data'));
        const groups = JSON.parse(this.getAttribute('data-chart_groups'));
        const stacked = this.getAttribute('data-stacked') === '1';

        this.questionId = questionId;

        this.renderChart(data, questionId, stacked, answerType);

        updateChartFilters(this.chart, groups);
    }

    renderChart(data, questionId, stacked, answerType) {
        const chartType = 'bar';

        this.shadowRoot.querySelector('#default-chart-container').innerHTML = '';
        const canvas = document.createElement('canvas');
        canvas.classList.add('exp-card-chart__chart');
        canvas.classList.add('js-card-chart-canvas');
        canvas.id = 'js-chart-id-' + questionId;

        const borderWidthValue = 0;
        const hoverOffsetValue = 0;

        const originalChartOptions = {
            indexAxis: 'y',
            tension: 0.4,
            pointRadius: 0,
            aspectRatio: 2,
            responsive: true,
            maintainAspectRatio: false,
            borderWidth: borderWidthValue,
            hoverOffset: hoverOffsetValue,
            interaction: {
                intersect: chartType !== 'line',
                mode: 'index',
            },
            plugins: {
                legend: {
                    position: 'bottom',
                    labels: {
                        color: '#595959',
                        font: {
                            family: 'PP Object Sans',
                            size: 11,
                            weight: 'normal',
                            lineHeight: 2,
                        },
                        usePointStyle: true,
                        pointStyle: 'rect',
                    },
                },
                tooltip: {
                    padding: 12,
                    borderWidth: 1,
                    cornerRadius: 8,
                    borderColor: '#e6edff',
                    backgroundColor: '#fff',
                    titleColor: '#333',
                    titleFont: {
                        family: 'PP Object Sans',
                        size: 10,
                        weight: 'bold',
                    },
                    bodyColor: '#595959',
                    bodyFont: {
                        family: 'PP Object Sans',
                        size: 10,
                        weight: 'normal',
                    },
                    usePointStyle: true,
                    pointStyle: 'rect',
                    callbacks: {
                        label: function (context) {
                            return formatTooltipLabel(context, answerType);
                        },
                    },
                },
            },
        };

        let newChartOptions = {};
        if (chartType !== 'doughnut') {
            newChartOptions = {
                scales: {
                    x: {
                        grid: {
                            display: false,
                        },
                        ticks: {
                            color: '#595959',
                            font: {
                                family: 'PP Object Sans',
                                size: 10,
                                weight: 'normal',
                                lineHeight: 2,
                            },
                        },
                    },
                    y: {
                        ticks: {
                            color: '#333',
                            font: {
                                family: 'PP Object Sans',
                                size: 10,
                                weight: 'bold',
                                lineHeight: 2,
                            },
                        },
                    },
                },
            };
        }

        if (chartType === 'bar') {
            newChartOptions = {
                scales: {
                    x: {
                        stacked: stacked,
                        grid: {
                            display: false,
                        },
                        ticks: {
                            callback: value => {
                                if (answerType === 'Percentage') {
                                    return `${value}%`;
                                }

                                return value;
                            },
                        },
                    },
                    y: {
                        stacked: stacked,
                        max: getMaxYAxisForAnswerType(answerType),
                    },
                },
            };
        }

        if (chartType === 'line') {
            newChartOptions = {
                scales: {
                    x: {
                        grid: {
                            display: false,
                        },
                    },
                    y: {
                        ticks: {
                            callback: value => {
                                if (answerType === 'Percentage') {
                                    return `${value}%`;
                                }

                                return value;
                            },
                        },
                    },
                },
            };
        }

        const finalChartOptions = {
            ...originalChartOptions,
            ...newChartOptions,
        };

        this.shadowRoot.querySelector('#default-chart-container').appendChild(canvas);

        const datasets = Array.from({ length: data.length }, (_, i) => {
            return generateDataset(data[i].answer_label, [data[i].answer_value], [chartColors[i % data.length]]);
        });

        this.chart = new Chart(canvas.getContext('2d'), {
            type: chartType,
            data: {
                labels: ['Totaal'],
                datasets: datasets,
            },
            options: finalChartOptions,
        });

        this.initChartDownloadCsv();
    }

    render() {
        this.shadowRoot.innerHTML = `
            <style>
                .exp-card-chart__body {
                    position: relative;
                    margin-top: 16px;
                    margin-bottom: 24px;
                    height: 500px;
                }
            </style>
            <div id="default-chart-container" class="exp-card-chart__body">Aan het laden...</div>
        `;
    }

    disconnectedCallback() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }
}

customElements.define('default-chart-component', DefaultChartComponent);
